import { useState } from "react";
import delay from "lodash/delay";
import styled from "styled-components";
import { Button, Dialog } from "@blueprintjs/core";
import { faCheckCircle, faCopy } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ConfigSnippetDialogContent } from "@pages/setup/components/ConfigSnippetDialogContent";
import { StepItem } from "@pages/setup/components/StepItem/StepItem";
import { CIProvider } from "@pages/setup/types";

export interface CIProviderStepTwoFileProps {
  ciProvider: CIProvider;
  whereToAdd: string;
  configFileName: string;
  customConfigHref: string;
  configYaml: string;
}

const Buttons = styled.div`
  display: flex;
  gap: 20px;
`;

/**
 * Returns StepItem component that allows the user to view/copy the config file
 * Also controls visibility of dialog with full config file
 */
export const StepTwoFile: React.FC<CIProviderStepTwoFileProps> = (props) => {
  const [isSnippetVisible, setIsSnippetVisible] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard
      .writeText(props.configYaml)
      .then(() => {
        setIsCopied(true);
      })
      .then(() => {
        // wait for two seconds, then set back to false
        delay(setIsCopied, 2000, false);
      });
  };

  return (
    <>
      <Dialog
        isOpen={isSnippetVisible}
        onClose={() => setIsSnippetVisible(false)}
      >
        <ConfigSnippetDialogContent
          ciProvider={props.ciProvider}
          configYaml={props.configYaml}
          customConfigHref={props.customConfigHref}
        />
      </Dialog>
      <StepItem
        number={2}
        title={`Add Semgrep to ${props.whereToAdd}`}
        description={
          <div>
            {`Choose a project and append the snippet to your ${props.configFileName} or `}
            <a
              href={props.customConfigHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              create a custom config file
            </a>{" "}
            for Semgrep.
          </div>
        }
        button={
          <Buttons>
            <Button
              onClick={copyCode}
              icon={
                <FontAwesomeIcon icon={isCopied ? faCheckCircle : faCopy} />
              }
            >
              {isCopied ? "Copied" : "Copy snippet"}
            </Button>
            <Button
              minimal
              style={{ color: "var(--link-color)" }}
              onClick={() => setIsSnippetVisible(!isSnippetVisible)}
            >
              Review snippet
            </Button>
          </Buttons>
        }
      />
    </>
  );
};
